import { jam } from '@jam.dev/sdk'
import CssBaseline from '@material-ui/core/CssBaseline'
import * as Sentry from '@sentry/react'
import { Amplify } from 'aws-amplify'
import 'fontsource-roboto'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import { Provider } from 'react-redux'
import {
	createBrowserRouter,
	createRoutesFromChildren,
	matchRoutes,
	useLocation,
	useNavigationType,
} from 'react-router'
import 'react-data-grid/lib/styles.css'
import 'react-toastify/dist/ReactToastify.css'

import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import App from 'src/App'
import type { UserInfo } from 'src/types/miscType'
import isProduction from 'src/utils/checkEnvironment'
import { captureExceptionAndThrow } from 'src/utils/posthogException'
import packageJson from '../package.json'
import config from './aws-exports'
import './i18n/i18n'
import './utils/i18nGlobals'
import store from './redux/store'

const posthogClientKey = process.env.REACT_APP_PUBLIC_POSTHOG_KEY || ''
const posthogHost = process.env.REACT_APP_PUBLIC_POSTHOG_HOST || ''
const isPosthogConfigured = Boolean(posthogClientKey && posthogHost)

if (isPosthogConfigured && process.env.USER_BRANCH === 'production') {
	posthog.init(posthogClientKey, {
		api_host: posthogHost,
		defaults: '2025-05-24',
		capture_pageview: true,
		capture_pageleave: 'if_capture_pageview',
		capture_exceptions: true,
		enable_recording_console_log: true,
		session_recording: {
			maskAllInputs: false,
		},
		error_tracking: {
			captureExtensionExceptions: false,
		},
		loaded: (client) => {
			client.startSessionRecording(true)
		},
	})
} else {
	posthog.opt_out_capturing()
}

const pickDefinedPersonProperties = (userInfo: UserInfo) => {
	const personProperties = {
		email: userInfo.attributes?.email ?? userInfo.username,
		tenantId: userInfo.tenantId ?? userInfo.currentCompanyTenantId,
		currentCompanyTenantId: userInfo.currentCompanyTenantId ?? undefined,
		currentCompanyName: userInfo.currentCompanyName ?? undefined,
		groups: userInfo.groups ?? undefined,
	}

	return Object.fromEntries(
		Object.entries(personProperties).filter(([, value]) => {
			return value !== undefined && value !== null
		})
	)
}

let lastPosthogUserId: string | null = null
let lastPosthogPersonPropertiesSnapshot: string | null = null

const syncPosthogIdentity = (
	stateAuth: ReturnType<typeof store.getState>['auth']
) => {
	if (!isPosthogConfigured) return
	const userInfo = stateAuth.userInfo
	const hasLoggedInUser = Boolean(stateAuth.userLoginStatus && userInfo?.id)

	if (!hasLoggedInUser) {
		if (lastPosthogUserId) {
			posthog.reset()
			lastPosthogUserId = null
			lastPosthogPersonPropertiesSnapshot = null
		}
		return
	}

	const nextUserId = userInfo.id as string
	const personProperties = pickDefinedPersonProperties(userInfo)
	const propertiesSnapshot = JSON.stringify(personProperties)

	if (lastPosthogUserId !== nextUserId) {
		posthog.identify(nextUserId, personProperties)
		lastPosthogUserId = nextUserId
		lastPosthogPersonPropertiesSnapshot = propertiesSnapshot
		return
	}

	if (lastPosthogPersonPropertiesSnapshot !== propertiesSnapshot) {
		posthog.setPersonProperties(personProperties)
		lastPosthogPersonPropertiesSnapshot = propertiesSnapshot
	}
}

Amplify.configure(config)

if (isProduction()) {
	Sentry.init({
		dsn: 'https://647a34b7215147c3a54ccc7723eb8887@o912551.ingest.sentry.io/4504683921014784',
		// This sets the sample rate to be 10%. You may want this to be 100% while
		// in development and sample at a lower rate in production
		replaysSessionSampleRate: 0,
		// If the entire session is not sampled, use the below sample rate to sample
		// sessions when an error occurs.
		replaysOnErrorSampleRate: 1.0,
		normalizeDepth: 100,
		normalizeMaxBreadth: 2000,
		ignoreErrors: [
			'ResizeObserver loop limit exceeded',
			'TypeError: Failed to fetch',
			'TypeError: NetworkError when attempting to fetch resource.',
			'ResizeObserver loop completed with undelivered notifications.',
		],
		integrations: [
			Sentry.reactRouterV7BrowserTracingIntegration({
				useEffect: React.useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes,
			}),
		],
		tracesSampleRate: 1.0,
		release: process.env.REACT_APP_SENTRY_RELEASE,
	})
}

// Subscribe to the store to get updated state
store.subscribe(() => {
	const updatedState = store.getState()
	jam.metadata(() => {
		return {
			userInfo: updatedState.auth.userInfo,
			isOffline: updatedState.auth.isOffline,
			buildDate: JSON.stringify(packageJson.buildDate),
			timeSincePageLoadSeconds: Number.parseInt(
				`${performance.now() / 1000}`
			),
		}
	})
	syncPosthogIdentity(updatedState.auth)
})

syncPosthogIdentity(store.getState().auth)

export const sentryCreateBrowserRouter =
	Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)

const rootElement = document.getElementById('root')
if (!rootElement) {
	captureExceptionAndThrow(new Error('Failed to find the root element'), {
		location: 'index.tsx bootstrap',
		rootElementId: 'root',
		isPosthogConfigured,
	})
}
const root = createRoot(rootElement)

root.render(
	<Provider store={store}>
		<PostHogProvider client={posthog}>
			<HelmetProvider>
				<CssBaseline />
				<App />
			</HelmetProvider>
		</PostHogProvider>
	</Provider>
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
// serviceWorkerRegistration.register()

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals()
